'use client';

import { RefObject, useEffect, useRef } from 'react';

import { Link } from '@/components/atoms';
import { cn } from '@/lib/utils/tailwind';

import type { Page } from '@/lib/api/types';

export interface NavbarLinksProps {
  pages: Page[];
  contextualAction?: () => void;
  setIsOverflowing?: (overflow: boolean) => void;
  wrapperRef?: RefObject<HTMLDivElement | null>;
}

export const NavbarLinks = ({
  pages,
  contextualAction,
  setIsOverflowing,
  wrapperRef,
}: NavbarLinksProps) => {
  const contentRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!setIsOverflowing || !wrapperRef?.current || !contentRef.current)
      return;

    const wrapper = wrapperRef.current;
    const content = contentRef.current;

    const checkOverflow = () => {
      const isOverflowing = content.scrollWidth > wrapper.clientWidth;
      setIsOverflowing(isOverflowing);
    };

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(wrapper);
    resizeObserver.observe(content);

    // Initial check
    checkOverflow();

    window.addEventListener('resize', checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkOverflow);
    };
  }, [setIsOverflowing, wrapperRef]);

  const visiblePages = pages
    .filter(page => page.menu.displayed && !page.is_signup)
    .filter(Boolean);

  return (
    <ul
      ref={contentRef}
      className={cn(
        'p-3',
        // Because of specificity issues, I am having to apply link styling at the UL level here within this component and target all of the descendants of it. See here for details: https://tailwindcss.com/docs/hover-focus-and-other-states#styling-all-descendants
        '**:text-(--navigation-link-color-default)',
        '**:[text-decoration:var(--navigation-link-decoration-default)]',
        '**:[text-transform:var(--navigation-link-transform)]',
        '**:hover:[text-decoration:var(--navigation-link-decoration-hover)]',
        '**:hover:text-(--navigation-link-color-hover)',
        '**:active:[text-decoration:var(--navigation-link-decoration-active)]',
        '**:active:text-(--navigation-link-color-active)',
        '**:visited:[text-decoration:var(--navigation-link-decoration-visisted)]',
        '**:visited:text-(--navigation-link-color-visited)',

        wrapperRef?.current === undefined
          ? 'mb-4 mt-2 flex flex-col gap-2'
          : 'flex my-0 flex-row items-center gap-(--navigation-link-gap) whitespace-nowrap',
        'overflow-visible',
      )}
    >
      {visiblePages.map(page => {
        const slug = page.is_home ? '/' : `/${page.slug}`;

        // color /
        return (
          <li key={`menu-link-${page.id}`}>
            <Link
              onClick={contextualAction}
              href={slug}
              size="md"
              className={cn(
                'inline-flex items-center',
                'bg-(--navigation-link-background-default)',
                'hover:bg-(--navigation-link-background-hover)',
                'active:bg-(--navigation-link-background-active)',
                'rounded-(--navigation-link-radius)',
                'px-(--navigation-link-hspacing)',
                'py-(--navigation-link-vspacing)',
              )}
            >
              {page.menu.name}
            </Link>
          </li>
        );
      })}
    </ul>
  );
};
